import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, Clock, DollarSign, FileText, Scale, Settings } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Tabs } from '../../components/ui/Tabs';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import labourService from '../../services/labour';
import type { LabourSettings } from '../../types/labour';
import LabourReportPanel from '../../components/labour/LabourReportPanel';
import TimesheetCheckPanel from '../../components/labour/TimesheetCheckPanel';
import TimeRecordsPanel from '../../components/labour/TimeRecordsPanel';
import StatementsPanel from '../../components/labour/StatementsPanel';
import RatesPanel from '../../components/labour/RatesPanel';
import SetupPanel from '../../components/labour/SetupPanel';

type TabKey = 'report' | 'check' | 'records' | 'statements' | 'rates' | 'setup';

const ALL_TABS: { key: TabKey; label: string; icon: ReactNode; adminOnly?: boolean }[] = [
  { key: 'report', label: 'Hours & costs', icon: <BarChart3 className="h-4 w-4" /> },
  { key: 'check', label: 'Timesheet check', icon: <Scale className="h-4 w-4" /> },
  { key: 'records', label: 'Time records', icon: <Clock className="h-4 w-4" /> },
  { key: 'statements', label: 'Contractor statements', icon: <FileText className="h-4 w-4" />, adminOnly: true },
  { key: 'rates', label: 'Rates', icon: <DollarSign className="h-4 w-4" />, adminOnly: true },
  { key: 'setup', label: 'Setup', icon: <Settings className="h-4 w-4" /> },
];

/**
 * /jobs/labour — where the hours went and what they cost: by block, cultivar
 * area, cultivar, type of work, person, provider or crew. Gated in App.tsx on
 * a farm_jobs supervisor / creator / admin seat; every dollar figure, rates and
 * contractor statements are Farm Jobs Admin only (the server enforces it too).
 */
export default function Labour() {
  const [params, setParams] = useSearchParams();
  const { hasAccess: isAdmin } = useModuleLicense('farm_jobs', 'admin');
  const tabs = ALL_TABS.filter(t => !t.adminOnly || isAdmin);
  const requested = params.get('tab') as TabKey | null;
  const tab: TabKey = tabs.some(t => t.key === requested) ? (requested as TabKey) : 'report';

  const [settings, setSettings] = useState<LabourSettings>({
    currency: 'AUD', on_cost_pct: 0, tax_name: 'GST', tax_rate_pct: 0, costs_include_tax: false,
  });
  const loadSettings = useCallback(() => {
    labourService.getSettings().then(s => { if (s.settings) setSettings(s.settings); }).catch(() => {});
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  return (
    <>
      <PageHeader
        title="Labour"
        subtitle="Hours and cost by block, cultivar and type of work"
        icon={<Clock className="h-6 w-6 text-white" />}
      />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs
          tabs={tabs}
          active={tab}
          onChange={k => setParams(k === 'report' ? {} : { tab: k }, { replace: true })}
          className="mb-4 print:hidden"
        />
        <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
          {tab === 'report' && <LabourReportPanel />}
          {tab === 'check' && <TimesheetCheckPanel />}
          {tab === 'records' && <TimeRecordsPanel />}
          {tab === 'statements' && <StatementsPanel />}
          {tab === 'rates' && <RatesPanel settings={settings} />}
          {tab === 'setup' && <SetupPanel canEdit={isAdmin} onSettingsChanged={loadSettings} />}
        </div>
      </div>
    </>
  );
}
